import { useState, FocusEventHandler, MouseEvent } from "react"
import styled, { keyframes } from "styled-components"

import Row from "../../../../components/layout/Row"
import SvgLogo from "../../../../app/assets/img/logo.svg"

interface NavItem {
  label: string
  href: string
  description?: string
}

const serviceLinks: NavItem[] = [
  {
    label: "Gestion locative",
    href: "/gestion-locative-portneuf",
    description: "Immeubles résidentiels à Portneuf et dans l’ouest de Québec",
  },
  {
    label: "Gestion de chalets et condos",
    href: "/gestion-chalets-condos-portneuf",
    description: "Suivi des séjours et de l’entretien",
  },
  {
    label: "Guides pour propriétaires",
    href: "/guide-proprietaires-portneuf",
    description: "Listes pratiques, conseils et ressources locales",
  },
]

const CALLBACK_EVENT = "cta:open-callback"
const CALLBACK_ANCHOR_ID = "demander-un-rappel"
const CALLBACK_HASH = `#${CALLBACK_ANCHOR_ID}`

// Slide down animation for mobile menu
const slideDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// Container for the entire header
const HeaderLayout = styled.header`
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 10;
  background-color: #fff;
  border-bottom: 1px solid rgba(16, 55, 91, 0.12);
  box-shadow: 0 8px 24px rgba(8, 30, 54, 0.06);
`

const SkipLink = styled.a`
  position: fixed;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 1000;
  padding: 0.75rem 1rem;
  border-radius: 2px;
  background: #ffffff;
  color: ${({ theme }) => theme.primaryDark};
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 30px rgba(8, 30, 54, 0.2);
  transform: translateY(-150%);

  &:focus {
    transform: translateY(0);
  }
`

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.35rem 1.4rem;
  background: #f3f7fb;
  border-bottom: 1px solid rgba(27, 117, 188, 0.12);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;

  @media (max-width: 960px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.3rem;
    font-size: 0.68rem;
    letter-spacing: 0.08em;
  }
`

const TopBarTagline = styled.span`
  color: ${({ theme }) => theme.primaryDark};
  font-weight: 600;
`

const TopBarEmergency = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: ${({ theme }) => theme.textPrimary};
  text-transform: none;
  letter-spacing: 0.04em;

  @media (max-width: 960px) {
    letter-spacing: 0.02em;
  }
`

const TopBarPhone = styled.a`
  font-weight: 700;
  color: ${({ theme }) => theme.primary};
  text-decoration: none;
  letter-spacing: 0.02em;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.primaryDark};
  }
`

const HeaderMain = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 88px;
  padding: 0.45rem 0;
`

// Logo container
export const Logo = styled.a`
  display: flex;
  align-items: center;
  font-size: 1.8rem;
  font-weight: bold;
  color: ${({ theme }) => theme.primary};
  text-decoration: none;
  padding: 0 20px;

  svg {
    max-width: 190px;
    width: 100%;
    height: auto;
    transition: width 0.3s ease;
  }
`

// Nav for desktop view
const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0 20px;

  @media (max-width: 960px) {
    display: none;
  }
`

const NavItemDesktop = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  /* padding-bottom: 28px; */
`

// Nav links styling
const NavLink = styled.a`
  font-size: 1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.primary};
  }
`

const SubMenu = styled.div<{ $open: boolean }>`
  display: ${({ $open }) => ($open ? "grid" : "none")};
  grid-template-columns: 1fr;
  gap: 0;
  padding: 8px;
  position: absolute;
  top: calc(100% - 4px);
  left: 0;
  min-width: 280px;
  background-color: #ffffff;
  border: 1px solid rgba(16, 55, 91, 0.14);
  border-top: 3px solid ${({ theme }) => theme.primary};
  box-shadow: 0 20px 48px rgba(8, 30, 54, 0.13);
  border-radius: 2px;
  z-index: 15;
`

const SubMenuLink = styled.a`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-decoration: none;
  padding: 10px 12px;
  border-radius: 1px;
  color: ${({ theme }) => theme.textPrimary};
  transition:
    background-color 0.2s ease,
    color 0.2s ease;

  span {
    font-size: 0.82rem;
    color: ${({ theme }) => theme.textSecondary || theme.textPrimary};
    line-height: 1.4;
  }

  &:hover,
  &:focus {
    background-color: rgba(27, 117, 188, 0.08);
    color: ${({ theme }) => theme.primaryDark};
  }
`

// Hamburger Menu for mobile
const Hamburger = styled.button`
  display: none;
  cursor: pointer;
  padding: 0 20px;
  border: none;
  background: none;

  @media (max-width: 960px) {
    display: block;
  }

  div {
    width: 25px;
    height: 3px;
    background-color: ${({ theme }) => theme.textPrimary};
    margin: 4px;
    border-radius: 0;
    transition:
      transform 0.4s ease,
      opacity 0.4s ease;
  }
`

// Mobile menu
const MobileMenu = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? "flex" : "none")};
  flex-direction: column;
  position: absolute;
  top: calc(100% + 6px);
  right: 10px;
  left: 10px;
  background-color: #ffffff;
  box-shadow: 0 24px 40px rgba(0, 0, 0, 0.14);
  padding: 20px;
  border: 1px solid rgba(16, 55, 91, 0.14);
  border-radius: 2px;
  z-index: ${({ theme }) => theme.zIndex?.modal || 20};
  animation: ${slideDown} 0.3s ease forwards;

  @media (min-width: 961px) {
    display: none;
  }

  a {
    padding: 12px 0;
    font-size: 1.05rem;
    color: ${({ theme }) => theme.textPrimary};
    text-decoration: none;
    font-weight: 500;
    display: flex;
    flex-direction: column;
    gap: 6px;

    span {
      font-size: 0.85rem;
      color: ${({ theme }) => theme.textSecondary || theme.textPrimary};
      font-weight: 400;
    }
  }
`

const MobileCTAButton = styled(NavLink)`
  margin-top: 12px;
  padding: 14px 18px;
  border-radius: 2px;
  background: ${({ theme }) => theme.primaryDark};
  color: ${({ theme }) => theme.white};
  border: 1px solid rgba(99, 195, 255, 0.35);
  font-weight: 600;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 24px rgba(13, 65, 114, 0.18);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  && {
    align-items: center;
    padding: 14px 18px;
    color: #ffffff;
  }

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.32), transparent 65%);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.white};
    transform: translateY(-1px);
    box-shadow: 0 16px 30px rgba(13, 65, 114, 0.24);
  }

  &:hover::before,
  &:focus::before {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid rgba(99, 195, 255, 0.7);
    outline-offset: 3px;
  }
`

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.primaryDark};
  color: ${({ theme }) => theme.white};
  font-size: 1rem;
  font-weight: 600;
  height: 42px;
  padding: 0 22px;
  border-radius: 2px;
  border: 1px solid rgba(99, 195, 255, 0.35);
  text-decoration: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 12px 24px rgba(13, 65, 114, 0.2);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.3), transparent 65%);
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 30px rgba(13, 65, 114, 0.28);
  }

  &:hover::before {
    opacity: 1;
  }

  &:focus-visible {
    outline: 2px solid rgba(99, 195, 255, 0.7);
    outline-offset: 3px;
  }

  @media (max-width: 960px) {
    display: none;
  }
`

const LaunchButton = styled(CTAButton)`
  @media (max-width: 960px) {
    /* display: flex;
    width: 100%;
    justify-content: center; */
  }
`

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)

  const toggleMenu = () => {
    setMenuOpen((prev) => !prev)
  }

  const closeMenu = () => setMenuOpen(false)

  const handleServicesBlur: FocusEventHandler<HTMLDivElement> = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setServicesOpen(false)
    }
  }

  const handleCallbackClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (typeof window === "undefined") {
      return
    }

    const anchor = document.getElementById(CALLBACK_ANCHOR_ID)
    if (anchor) {
      event.preventDefault()
      window.dispatchEvent(new CustomEvent(CALLBACK_EVENT))
      anchor.scrollIntoView({ behavior: "smooth", block: "center" })
      return
    }

    window.location.href = `/${CALLBACK_HASH}`
  }

  return (
    <HeaderLayout data-nosnippet>
      <SkipLink href="#contenu-principal">Aller au contenu principal</SkipLink>
      <TopBar>
        <TopBarTagline>Gestion immobilière locale • Portneuf et Québec</TopBarTagline>
        <TopBarEmergency>
          Urgences locataires • 7 j/7
          <TopBarPhone href="tel:4186849004">(418) 684-9004</TopBarPhone>
        </TopBarEmergency>
      </TopBar>
      <HeaderMain>
        <Logo href="/" aria-label="TB Gestion Immobilière - retour à l'accueil">
          <SvgLogo />
        </Logo>
        <Row margin="0 0 0 auto" width="fit-content" height="100%">
          <Nav aria-label="Navigation principale">
            <NavItemDesktop>
              <NavLink href="/">Accueil</NavLink>
            </NavItemDesktop>
            <NavItemDesktop>
              <NavLink href="/a-propos">À propos</NavLink>
            </NavItemDesktop>
            <NavItemDesktop
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
              onFocus={() => setServicesOpen(true)}
              onBlur={handleServicesBlur}
            >
              <NavLink href="/services-gestion-immobiliere">Services</NavLink>
              <SubMenu $open={servicesOpen} onMouseEnter={() => setServicesOpen(true)} onMouseLeave={() => setServicesOpen(false)}>
                {serviceLinks.map((link) => (
                  <SubMenuLink key={link.href} href={link.href}>
                    {link.label}
                    {link.description && <span>{link.description}</span>}
                  </SubMenuLink>
                ))}
              </SubMenu>
            </NavItemDesktop>
            <NavItemDesktop>
              <NavLink href="/se-connecter">Espace TB</NavLink>
            </NavItemDesktop>
            <NavItemDesktop>
              <NavLink href="/nous-contacter">Nous contacter</NavLink>
            </NavItemDesktop>
          </Nav>
          <div style={{ display: "flex", alignItems: "center", paddingRight: "20px" }}>
            <LaunchButton href={`/${CALLBACK_HASH}`} onClick={handleCallbackClick}>
              Demander un rappel
            </LaunchButton>
          </div>
        </Row>

        <Hamburger onClick={toggleMenu} aria-label="Menu">
          <div
            style={{
              transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "rotate(0)",
            }}
          />
          <div style={{ opacity: menuOpen ? "0" : "1" }} />
          <div
            style={{
              transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "rotate(0)",
            }}
          />
        </Hamburger>

        <MobileMenu open={menuOpen}>
          {serviceLinks.map((link) => (
            <NavLink key={link.href} href={link.href} onClick={closeMenu}>
              {link.label}
              {link.description && <span>{link.description}</span>}
            </NavLink>
          ))}
          <NavLink href="/a-propos" onClick={closeMenu}>
            À propos
          </NavLink>
          <NavLink href="/nous-contacter" onClick={closeMenu}>
            Nous contacter
          </NavLink>
          <NavLink href="/se-connecter" onClick={closeMenu}>
            Espace TB
          </NavLink>
          <NavLink href="tel:4186849004" onClick={closeMenu}>
            Ligne d'urgence
            <span>(418) 684-9004</span>
          </NavLink>
          <MobileCTAButton
            href={`/${CALLBACK_HASH}`}
            onClick={(event) => {
              handleCallbackClick(event)
              closeMenu()
            }}
          >
            Demander un rappel
          </MobileCTAButton>
        </MobileMenu>
      </HeaderMain>
    </HeaderLayout>
  )
}

export default Header
